import React, { useState } from 'react';
import { message as AntdMessage } from "antd";
import { useNavigate } from "react-router-dom";
import './SecurityDataPolicy.css';

const SecurityDataPolicy = () => {
  return (
    <div className="sm-pageWrapper">
      <h1 className="sm-mainTitle">Security Measures for CollabAI</h1>
      <div className="sm-termsContainer">
        <div className="sm-contentContainer">
          <div className="sm-content">
            <div className="sm-section">
              <p className="sm-paragraph">
                At CollabAI, we prioritize security and data protection to ensure the highest level of trust and reliability. Our comprehensive security measures safeguard sensitive information, prevent unauthorized access, and maintain the integrity of our infrastructure. Whether using our SaaS platform or deploying CollabAI on your cloud infrastructure, we implement industry-leading security practices to protect your data.
              </p>
              <h2 className="sm-sectionTitle">Security Measures for CollabAI SaaS</h2>
              <div className="sm-divider"></div>
              <h3 className="sm-sectionTitle">Production Database Authentication</h3>
              <p className="sm-paragraph">
                CollabAI enforces stringent authentication for its production databases, allowing access only through authorized methods such as unique SSH keys and username/password combinations. Security groups further restrict database access to approved personnel.
              </p>
              <h3 className="sm-sectionTitle">Advanced Encryption for Secure Data Storage</h3>
              <p className="sm-paragraph">
                We safeguard all knowledge base data, chats, and relevant information in our encrypted database hosted on Amazon Web Services (AWS), utilizing industry-leading encryption standards.
              </p>
              <h3 className="sm-sectionTitle">Restricted Production Application Access</h3>
              <p className="sm-paragraph">
                Access to the CollabAI application is strictly limited to authorized users, preventing unauthorized individuals from gaining entry into the system.
              </p>
              <h3 className="sm-sectionTitle">Established Access Control Procedures</h3>
              <p className="sm-paragraph">
                Our structured access control policy defines how user access is managed, ensuring proper authorization and oversight, including:
                <ul>
                  <li>Adding new users</li>
                  <li>Modifying existing user privileges</li>
                  <li>Removing access when no longer required</li>
                </ul>
              </p>
              <h3 className="sm-sectionTitle">Operating System & Network Access Restrictions</h3>
              <p className="sm-paragraph">
                Privileged access to the operating system and production network is strictly limited to authorized users who require it for operational purposes.
              </p>
              <h3 className="sm-sectionTitle">Remote Access Authentication</h3>
              <p className="sm-paragraph">
                Remote access to CollabAI’s production environment is allowed solely for authorized employees through approved and secure connections, minimizing risk.
              </p>
              <h3 className="sm-sectionTitle">Infrastructure Performance Monitoring</h3>
              <p className="sm-paragraph">
                We utilize monitoring tools to continuously oversee system performance and generate alerts when predefined thresholds are reached.
              </p>
              <h3 className="sm-sectionTitle">Regular Firewall Rule Reviews</h3>
              <p className="sm-paragraph">
                Firewall rules are reviewed quarterly to maintain optimal security, with necessary changes identified and tracked through completion.
              </p>
              <h3 className="sm-sectionTitle">Maintained Service Infrastructure</h3>
              <p className="sm-paragraph">
                Routine maintenance and timely patching of our infrastructure ensure that our systems remain protected against security threats and vulnerabilities.
              </p>
              <h3 className="sm-sectionTitle">Business Continuity and Disaster Recovery (BC/DR) Plans</h3>
              <p className="sm-paragraph">
                CollabAI has a clear plan for maintaining business continuity and recovering from disasters, with at least annual testing to verify effectiveness.
              </p>
              <h3 className="sm-sectionTitle">Enforced Change Management Procedures</h3>
              <p className="sm-paragraph">
                All modifications to CollabAI’s software and infrastructure must undergo a structured process, including:
                <ul>
                  <li>Authorization</li>
                  <li>Documentation</li>
                  <li>Testing</li>
                  <li>Review and approval before implementation</li>
                </ul>
              </p>
              <h3 className="sm-sectionTitle">Communication of System Changes</h3>
              <p className="sm-paragraph">
                Customers are notified of critical system changes that may impact their operations, while authorized internal users receive relevant system updates.
              </p>
              <h3 className="sm-sectionTitle">Conducted Access Reviews</h3>
              <p className="sm-paragraph">
                Access reviews are conducted quarterly for all system components, with necessary modifications identified and implemented to maintain secure access controls.
              </p>
              <h2 className="sm-sectionTitle">Securely Hosting CollabAI on Your Cloud Infrastructure</h2>
              <div className="sm-divider"></div>
              <p className="sm-paragraph">
                CollabAI gives you the flexibility to deploy on your cloud infrastructure, ensuring maximum security and compliance with your internal policies. Whether hosted on a private cloud, a public cloud provider, or an on-premise server, CollabAI is designed with robust security best practices to keep your data protected.
              </p>
              <h3 className="sm-sectionTitle">Why Self-Hosting CollabAI is Secure?</h3>
              <h4 className="sm-subSectionTitle">Full Data Ownership & Control</h4>
              <p className="sm-paragraph">
                Hosting CollabAI on your cloud infrastructure ensures complete control over your data, eliminating third-party access while ensuring compliance with internal security policies and regulatory requirements.
              </p>
              <h4 className="sm-subSectionTitle">Secure Cloud Deployment</h4>
              <p className="sm-paragraph">
                CollabAI is optimized for cloud environments, leveraging industry-leading security measures, including:
                <ul>
                  <li>Regular Security Patches to stay protected against threats.</li>
                  <li>Built-in Firewall & Network Controls for granular access control.</li>
                  <li>Role-Based Access Control (RBAC) to define user permissions and restrict sensitive operations.</li>
                </ul>
              </p>
              <h4 className="sm-subSectionTitle">Advanced Encryption Standards</h4>
              <p className="sm-paragraph">
                <ul>
                  <li>Data at Rest Encryption: Protect stored data with AES-256 encryption.</li>
                  <li>Data in Transit Encryption: Secure communication via TLS 1.2/1.3.</li>
                  <li>Database Access Restrictions: Only authorized applications and users can access stored information.</li>
                  <li>Advanced Encryption for Secure Data Storage: All knowledge base data, chats, and relevant information are stored in encrypted databases hosted on your cloud infrastructure.</li>
                </ul>
              </p>
              <h4 className="sm-subSectionTitle">Established Access Control Procedures</h4>
              <p className="sm-paragraph">
                <ul>
                  <li>Adding new users</li>
                  <li>Modifying existing user privileges</li>
                  <li>Removing access when no longer required</li>
                </ul>
              </p>
              <h4 className="sm-subSectionTitle">Customizable Security Policies</h4>
              <p className="sm-paragraph">
                <ul>
                  <li>Implement SSH Key Authentication to secure remote access.</li>
                  <li>Set up Intrusion Detection Systems (IDS) for proactive threat monitoring.</li>
                  <li>IP Whitelisting & Network Segmentation to restrict unauthorized access and separate internal resources from external exposure.</li>
                </ul>
              </p>
              <h4 className="sm-subSectionTitle">Continuous Monitoring & Compliance</h4>
              <p className="sm-paragraph">
                <ul>
                  <li>Log & Audit Trails: Maintain transparency with detailed security logs.</li>
                  <li>Automated Backups & Disaster Recovery: Utilize cloud backup solutions to ensure data safety against failures or cyber threats.</li>
                </ul>
              </p>
              <h3 className="sm-sectionTitle">Installation & Support for Cloud-Hosted Servers</h3>
              <p className="sm-paragraph">
                Deploying CollabAI on your cloud infrastructure is seamless. Our support team is available to guide you through:
                <ul>
                  <li>Installation & Configuration Assistance</li>
                  <li>Security Hardening Recommendations</li>
                  <li>Performance Optimization & Monitoring Setup</li>
                </ul>
              </p>
              <p className="sm-paragraph">
                CollabAI remains committed to maintaining a secure environment by implementing rigorous security controls and best practices. Our ongoing efforts ensure data protection, system integrity, and compliance with industry security standards.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityDataPolicy;